import cookieHandler from '../utils/cookieHandler.js';
import { personDetails } from '../helper/PersonDetails.js';

/**
 * Apply a coupon to an order.
 *
 * @param {string} [couponCode=''] - Optional coupon code applied to the order.
 * @param {UserData | {}} [userData] - Userdata if user logged in or null.
 * @param {EventiveEvent} eventData - The current Event object.
 * @param {CouponResponse | {}} [couponResult] - Das Ergebnis des Coupon-Tests (Rabattdaten).
 * @returns {Promise<Order>} - Die erstellte Bestellung.
 */
export async function setEventOrder(eventData, couponCode = '', userData = {}, couponResult = {}, eventPass) {
  // Extrahiere die Subitems (Tickets + Varianten) aus den Ticket-Buckets
  const subitems = getSubitemsFromBuckets(eventData.ticket_buckets);
  const eventiveToken = cookieHandler.getCookie('eventive-token');
  const userToken = userData?.token ?? eventiveToken ?? '';
  /** @type {PersonDetails} personDetailsPayLoad */
  const personDetailsPayLoad = personDetails.get();
  const personName = personDetailsPayLoad?.user?.name ? personDetailsPayLoad.user.name : '';
  const personMail = personDetailsPayLoad?.user?.email ? personDetailsPayLoad.user.email : '';
  const email = personMail ? personMail : userData?.details?.email ? userData.details.email : '';
  const name = personName ? personName : userData?.details?.name ? userData.details.name : '';

  /**
   * @type {Order} order
   */
  let order = {
    subitems,
    event: eventData.id,
    ...(email && { emails: [email] }),
    ...(name && { name: name }),
    ...(couponCode && { coupon: couponCode }),
    ...(userToken && { token: userToken }),
    ...(eventPass?.id && {pass: eventPass.id}),
  };

  const subitemPrices = setSubitemPrices(eventData, order, couponResult);
  order = {
    ...order,
    ...(subitemPrices && { subitem_prices: subitemPrices }),
    ...(subitemPrices && { total: subitemPrices.reduce((total, item) => total + item.total, 0) }),
    ...(subitemPrices && { total_quantity: subitemPrices.reduce((total_quantity, item) => total_quantity + item.quantity, 0) }),
  };
  return order;
}

/**
 * Extrahiere die Subitems (Haupt-Tickets und Varianten) aus den Ticket-Buckets.
 *
 * @param {Array<TicketBucket>} ticketBuckets - Die Ticket-Buckets des Events.
 * @returns {Array.<object>} - Die Subitems für die Bestellung.
 */
function getSubitemsFromBuckets(ticketBuckets) {
  return ticketBuckets.flatMap(bucket => {
    const baseItem = getBaseItem(bucket);
    const variants = getVariantItems(bucket);
    return [baseItem, ...variants];
  });
}

/**
 * Erstellt ein Haupt-Item (Ticket ohne Variante) aus einem Bucket.
 *
 * @param {TicketBucket} bucket - Der Ticket-Bucket.
 * @returns {Object} - Das Haupt-Item.
 */
function getBaseItem(bucket) {
  return {
    ticket_bucket: bucket.id,
    quantity: getTicketQuantity(bucket.id),
  };
}

/**
 * Erstellt die Varianten-Items aus einem Bucket (falls vorhanden).
 *
 * @param {TicketBucket} bucket - Der Ticket-Bucket.
 * @returns {Array<Object>} - Die Varianten-Items.
 */
function getVariantItems(bucket) {
  if (!bucket || !Array.isArray(bucket.variants)) {
    return [];
  }
  return bucket?.variants?.map(variant => ({
    ticket_bucket: bucket.id,
    quantity: getTicketQuantity(variant.id),
    variant: variant.id,
  })) || [];
}

/**
 * Holt die Ticket-Menge aus dem DOM-Element anhand der Bucket-ID.
 *
 * @param {string} id - Die ID des Ticket-Buckets oder der Variante.
 * @returns {number} - Die Ticket-Menge.
 */
function getTicketQuantity(id) {
  const inputElement = document.getElementById(`quantity-${id}`);
  if (!inputElement) {
    console.error(`Element mit ID 'quantity-${id}' nicht gefunden!`);
    return 0; // Rückgabewert für nicht gefundene Elemente
  }
  return parseInt(inputElement.value, 10) || 0;
}

/**
 * Funktion zur dynamischen Anpassung der Preise basierend auf Eventdaten und Coupon-Ergebnissen.
 *
 * @param {EventiveEvent} eventData - Die Eventdaten, die zur Preisberechnung verwendet werden.
 * @param {Order} order - Die Auswahl des Benutzers (Tickets).
 * @param {CouponResponse | {}} couponResult - Das Ergebnis des Coupon-Tests (Rabattdaten).
 * @returns {Array.<SubitemPrice>} - Die berechneten Preise für die Subitems.
 */
function setSubitemPrices(eventData, order, couponResult = {}) {
  const subitems = order.subitems;

  return subitems.map((item, index) => {
    /**
     * @type {TicketBucket} bucket
     */
    const bucket = eventData.ticket_buckets.find(bucket => bucket.id === item.ticket_bucket);
    const variant = bucket?.variants && bucket.variants.find(v => v.id === item.variant) || {};
    const basePrice = variant.price || bucket.price;
    const variantName = variant?.name ? ` - ${variant.name}` : '';

    // Discount data for the current item.
    const discountData = couponResult?.subitems?.[index]?.price || {};
    let price = basePrice;

    // Check the pass rules from pass_adjusted here.
    const passId = order.pass;
    if (passId && bucket?.pass_adjusted?.[passId]) {
      const passAdjusted = bucket.pass_adjusted[passId];

      // If there are no errors, apply the pass price.
      if (passAdjusted.errors.length === 0 && passAdjusted.quantity_available > 0) {
        price = passAdjusted.price; // Set the price from pass_adjusted.
      }
    }

    // Consider max_quantity and current_quantity.
    const maxDiscountedQuantity = discountData.max_quantity ?? item.quantity;
    const discountedQuantity = Math.min(maxDiscountedQuantity, item.quantity);
    const nonDiscountedQuantity = item.quantity - discountedQuantity;

    // Apply discount if applicable.
    let discountedPrice = price;

    if (discountData.percent_off) {
      discountedPrice = price - (price * (discountData.percent_off / 100));
    } else if (discountData.cents_off) {
      discountedPrice = price - discountData.cents_off;
    } else if (discountData.cents_off_item) {
      discountedPrice = price - discountData.cents_off_item;
    } else if (discountData.set_price) {
      discountedPrice = discountData.set_price;
    }

    // Calculating the total for the subitem.
    const discountedTotal = discountedQuantity * discountedPrice;
    const nonDiscountedTotal = nonDiscountedQuantity * price;

    return {
      name: eventData?.name || '',
      subname: bucket?.name ? `${bucket.name}${variantName}` : '',
      start_time: eventData.start_time,
      inline_donation: false,
      variable_price: bucket?.variable_price ?? false,
      lock_admin_only: bucket?.lock_admin_only ?? false,
      original_price: basePrice, // The original price.
      price: discountedPrice, // The calculated price after discount (for the discounted part).
      quantity: item.quantity,
      fixed_quantity: false,
      fee: 0,
      tax: 0,
      total: discountedTotal + nonDiscountedTotal, // Total (discounted + non-discounted tickets).
      subitem: {
        ticket_bucket: item.ticket_bucket,
        variant: item.variant || null,
        inline_donation: false,
        quantity: item.quantity,
      },
    };
  });
}

